import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { SettingsLayout } from './SettingsLayout';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Globe, Unplug } from 'lucide-react';
import { API_URL } from '../../lib/api';
import { GradientBorder } from '../../components/ui/gradient-border';

interface ConnectedSite {
  id: string;
  name: string;
  url: string;
  environment: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  accessRole: 'owner' | 'admin' | 'member';
}

export function SettingsSitesPage() {
  const { isAuthenticated } = useAuth();
  const [sites, setSites] = useState<ConnectedSite[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinct from `error`: a failed LOAD must not be rendered as the
  // authoritative "no sites connected" empty state, since this page is the
  // security recovery path for spotting a rogue connection.
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');

  const [disconnectSite, setDisconnectSite] = useState<ConnectedSite | null>(
    null,
  );
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSites();
    }
  }, [isAuthenticated]);

  async function errorMessageFromResponse(
    res: Response,
    fallback: string,
  ): Promise<string> {
    const data = await res.json().catch(() => null);
    return (data && typeof data.error === 'string' && data.error) || fallback;
  }

  async function fetchSites() {
    try {
      setLoading(true);
      setError('');
      setLoadFailed(false);
      const res = await fetch(`${API_URL}/api/v2/cloud/connected-sites`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(
          await errorMessageFromResponse(res, 'Failed to fetch connected sites'),
        );
      }
      const data = (await res.json()) as { sites: ConnectedSite[] };
      setSites(data.sites);
    } catch (err) {
      setLoadFailed(true);
      setError(err instanceof Error ? err.message : 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  }

  async function handleDisconnect() {
    if (!disconnectSite || disconnecting) return;
    setDisconnecting(true);
    setError('');

    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/connected-sites/${disconnectSite.id}/disconnect`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (!res.ok) {
        throw new Error(
          await errorMessageFromResponse(res, 'Failed to disconnect site'),
        );
      }
      await fetchSites();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
      setDisconnectSite(null);
    }
  }

  function displayUrl(url: string): string {
    try {
      return new URL(url).host;
    } catch {
      return url;
    }
  }

  return (
    <SettingsLayout activeTab="sites">
      <div>
        <h1 className="text-2xl font-semibold text-white mb-2">Sites</h1>
        <p className="text-white/60 text-sm mb-6">
          These WordPress sites are connected to your Miles account. A connected
          site can run agents that spend your credits, so disconnect any site
          you don&apos;t recognize.
        </p>

        {error && (
          <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-white/50">
            Loading sites...
          </div>
        ) : loadFailed ? (
          <div className="rounded-xl border border-white/10 bg-card/50 p-8 text-center">
            <p className="text-white/60 mb-4">
              Couldn&apos;t load your connected sites.
            </p>
            <Button variant="outline" onClick={fetchSites}>
              Retry
            </Button>
          </div>
        ) : sites.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-card/50 p-8 text-center text-white/50">
            No WordPress sites are connected to your account.
          </div>
        ) : (
          <div className="space-y-3">
            {sites.map((site) => (
              <GradientBorder key={site.id}>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5">
                      <Globe className="h-4 w-4 shrink-0 text-white/40" />
                      <span className="text-white font-medium truncate">
                        {site.name}
                      </span>
                      {site.accessRole !== 'owner' && (
                        <span className="text-[10px] uppercase tracking-wider text-white/40 bg-white/5 px-1.5 py-0.5 rounded">
                          {site.accessRole}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 pl-[26px] text-xs text-white/50">
                      <a
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white/80 transition-colors truncate"
                      >
                        {displayUrl(site.url)}
                      </a>
                      {site.lastSeenAt && (
                        <span className="shrink-0">
                          Last active{' '}
                          {new Date(site.lastSeenAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDisconnectSite(site)}
                    className="ml-4 shrink-0 text-white/50 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Unplug className="h-4 w-4" />
                    Disconnect
                  </Button>
                </div>
              </GradientBorder>
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!disconnectSite}
        onOpenChange={() => setDisconnectSite(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect site</AlertDialogTitle>
            <AlertDialogDescription>
              {disconnectSite && (
                <>
                  This removes <strong>{disconnectSite.name}</strong>&apos;s
                  access to your Miles account. It can no longer run agents or
                  spend your credits until someone reconnects it.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDisconnectSite(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
}
